import { useEffect, useRef, useState } from 'react';
import { Copy, ShareNetwork, Warning, Drop, CheckCircle } from '@phosphor-icons/react';
import { getAsset, getAssetNetwork, NETWORKS, type AssetCode, type NetworkId } from '@onyx/shared';
import { useDepositInfo, useWallet, useMoneyMutation } from '../lib/queries';
import { useLang, useT, errorText } from '../lib/i18n';
import { formatCrypto } from '../lib/format';
import { standardTag } from '../lib/networks';
import { navigate } from '../lib/router';
import { api, ApiError } from '../lib/api';
import { copyText, haptic } from '../lib/telegram';
import { AssetList, NetworkList } from '../components/Pickers';
import { QR } from '../components/QR';
import { ErrorState, Facts, Notice, Screen, ScreenHeader, Skeleton, Slab, toast } from '../components/ui';

export function DepositPickAsset() {
  const t = useT();
  const wallet = useWallet();
  return (
    <Screen>
      <ScreenHeader title={t['deposit.title']} sub={t['pick.asset']} />
      <AssetList wallet={wallet.data} onPick={(code) => navigate(`/deposit/${code}`)} />
    </Screen>
  );
}

export function DepositPickNetwork({ asset }: { asset: AssetCode }) {
  const t = useT();
  const def = getAsset(asset)!;
  useEffect(() => {
    if (def.networks.length === 1) navigate(`/deposit/${asset}/${def.networks[0]!.network}`, { replace: true });
  }, [asset, def]);
  return (
    <Screen>
      <ScreenHeader title={`${t['deposit.title']} ${asset}`} sub={t['pick.network']} />
      <NetworkList asset={asset} mode="deposit" onPick={(n) => navigate(`/deposit/${asset}/${n.network}`)} />
    </Screen>
  );
}

function CopyBlock({ label, value }: { label: string; value: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (await copyText(value)) {
      haptic.success();
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };
  return (
    <button type="button" className={`copy-block${copied ? ' is-copied' : ''}`} onClick={() => void onCopy()} aria-label={`${t['deposit.copy']}: ${label}`}>
      <span className="copy-block__label">{label}</span>
      <span className="copy-block__value">
        {value}
      </span>
      <span className="copy-block__action">
        <Copy size={16} aria-hidden="true" /> {copied ? t['deposit.copied'] : t['deposit.copy']}
      </span>
    </button>
  );
}

/**
 * While this screen is open the wallet polls; when the network credits the
 * transfer the arrival is announced right here, no need to leave the screen.
 */
function ArrivalNotice({ asset }: { asset: AssetCode }) {
  const t = useT();
  const lang = useLang();
  const wallet = useWallet();
  const amount = Number(wallet.data?.balances.find((b) => b.asset === asset)?.available ?? 0);
  const baseline = useRef<number | null>(null);
  const [arrived, setArrived] = useState<number | null>(null);

  useEffect(() => {
    if (!wallet.isSuccess) return;
    if (baseline.current === null) {
      baseline.current = amount;
      return;
    }
    if (amount > baseline.current + 1e-12) {
      setArrived(amount - baseline.current);
      baseline.current = amount;
      haptic.success();
    }
  }, [amount, wallet.isSuccess]);

  if (arrived === null) return null;
  return (
    <Notice tone="good" icon={<CheckCircle size={18} weight="fill" />}>
      <span className="num">
        {t['deposit.arrived']} +{formatCrypto(String(arrived), getAsset(asset)!.decimals, lang)} {asset}
      </span>
    </Notice>
  );
}

export function DepositAddress({ asset, network }: { asset: AssetCode; network: NetworkId }) {
  const t = useT();
  const lang = useLang();
  const info = useDepositInfo(asset, network);
  const net = NETWORKS[network];
  const an = getAssetNetwork(asset, network);
  const faucet = useMoneyMutation((v: { asset: string; network: string; amount: string }) => api('/dev/deposit', { body: v }));
  const tag = an ? standardTag(an.standard) : null;
  const standard = tag ? ` (${tag})` : '';

  if (!an) return <ErrorState text={t['err.unknown']} />;

  return (
    <Screen className="deposit">
      <ScreenHeader title={`${t['deposit.title']} ${asset}`} sub={`${net.name}${standard}`} />
      <ArrivalNotice asset={asset} />
      {info.isPending ? (
        <div className="deposit__qr">
          <Skeleton w={232} h={232} r={20} />
        </div>
      ) : info.isError ? (
        <ErrorState text={t['common.error']} onRetry={() => void info.refetch()} />
      ) : (
        <>
          <div className="deposit__qr">
            <QR value={info.data.address} asset={asset} size={232} />
          </div>

          <Notice tone="warn" icon={<Warning size={18} weight="bold" />}>
            {t['deposit.onlyNetwork'](asset, `${net.name}${standard}`)}
          </Notice>

          <CopyBlock label={t['deposit.address']} value={info.data.address} />
          {info.data.memo && (
            <>
              <CopyBlock label={t['deposit.memo']} value={info.data.memo} />
              <Notice tone="bad" icon={<Warning size={18} weight="bold" />}>
                {t['deposit.memoWarn']}
              </Notice>
            </>
          )}

          <Facts
            items={[
              { label: t['deposit.min'], value: <span className="num">{formatCrypto(info.data.minDeposit, 8, lang)} {asset}</span> },
              { label: t['deposit.time'], value: `${t['deposit.confirmations'](info.data.confirmations)} · ${t['pick.minutes'](info.data.minutes)}` },
            ]}
          />

          <div className="deposit__actions">
            {typeof navigator.share === 'function' && (
              <Slab
                tone="quiet"
                icon={<ShareNetwork size={18} />}
                onClick={() => void navigator.share({ text: info.data.memo ? `${info.data.address}\nmemo: ${info.data.memo}` : info.data.address }).catch(() => {})}
              >
                {t['deposit.share']}
              </Slab>
            )}
            {info.data.testnet && import.meta.env.DEV && (
              <Slab
                tone="quiet"
                icon={<Drop size={18} />}
                loading={faucet.isPending}
                onClick={() =>
                  faucet.mutate(
                    { asset, network, amount: getAsset(asset)!.stable ? '100' : asset === 'BTC' ? '0.01' : asset === 'NOT' ? '5000' : '10' },
                    { onError: (e) => toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined)) },
                  )
                }
              >
                {t['deposit.faucet']}
              </Slab>
            )}
          </div>
        </>
      )}
    </Screen>
  );
}
